import { useEffect, useState, useCallback } from "react";
import {
  Search, Users, CheckCircle, XCircle, Trash2,
  ChevronLeft, ChevronRight, MoreVertical, Shield,
  GraduationCap, School, User,
} from "lucide-react";
import SuperAdminLayout from "@/components/ui/superadmin/SuperAdminLayout";
import { superAdminApi, type AdminUser } from "@/hooks/api/superAdminApi";

const STATUS_COLORS: Record<string, string> = {
  active:    "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  inactive:  "bg-slate-700/50 text-slate-400 border-slate-600",
  pending:   "bg-amber-500/10 text-amber-400 border-amber-500/20",
  suspended: "bg-red-500/10 text-red-400 border-red-500/20",
};

const TYPE_ICONS: Record<string, React.ElementType> = {
  student:     GraduationCap,
  school_admin: School,
  super_admin:  Shield,
  parent:       User,
};

const TYPE_COLORS: Record<string, string> = {
  student:      "text-violet-400 bg-violet-500/10",
  school_admin: "text-teal-400 bg-teal-500/10",
  super_admin:  "text-amber-400 bg-amber-500/10",
  parent:       "text-blue-400 bg-blue-500/10",
};

function UserTypeChip({ type }: { type: string }) {
  const Icon = TYPE_ICONS[type] ?? User;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${TYPE_COLORS[type] ?? "text-slate-400 bg-slate-700"}`}>
      <Icon className="w-3 h-3" />
      {type.replace("_", " ")}
    </span>
  );
}

function ActionMenu({ user, onSuspend, onActivate, onDelete }: {
  user: AdminUser;
  onSuspend: (id: string) => void;
  onActivate: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const isSuperAdmin = user.userType === "super_admin";

  return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        disabled={isSuperAdmin}
        className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 bg-slate-800 border border-slate-700 rounded-xl shadow-xl py-1 w-40">
            {user.status !== "active" ? (
              <button
                className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-emerald-400 hover:bg-slate-700 transition-colors"
                onClick={() => { setOpen(false); onActivate(user._id); }}
              >
                <CheckCircle className="w-3.5 h-3.5" /> Activate
              </button>
            ) : (
              <button
                className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-amber-400 hover:bg-slate-700 transition-colors"
                onClick={() => { setOpen(false); onSuspend(user._id); }}
              >
                <XCircle className="w-3.5 h-3.5" /> Suspend
              </button>
            )}
            <div className="my-1 border-t border-slate-700" />
            <button
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-red-400 hover:bg-slate-700 transition-colors"
              onClick={() => { setOpen(false); onDelete(user._id); }}
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete user
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function UsersManagement() {
  const [users, setUsers]       = useState<AdminUser[]>([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [pages, setPages]       = useState(1);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [typeFilter, setTypeFilter]     = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const res = await superAdminApi.listUsers({
        page: p, limit: 20,
        search: search || undefined,
        userType: typeFilter || undefined,
        status: statusFilter || undefined,
      });
      setUsers(res.users);
      setTotal(res.total);
      setPages(res.pages);
    } catch {
      // empty state handles display
    } finally {
      setLoading(false);
    }
  }, [page, search, typeFilter, statusFilter]);

  useEffect(() => { load(1); setPage(1); }, [search, typeFilter, statusFilter]);
  useEffect(() => { load(page); }, [page]);

  const handleSuspend = async (id: string) => {
    try {
      await superAdminApi.suspendUser(id);
      setUsers((prev) => prev.map((u) => u._id === id ? { ...u, status: "suspended" as const } : u));
    } catch {}
  };

  const handleActivate = async (id: string) => {
    try {
      await superAdminApi.activateUser(id);
      setUsers((prev) => prev.map((u) => u._id === id ? { ...u, status: "active" as const } : u));
    } catch {}
  };

  const handleDelete = async (id: string) => {
    try {
      await superAdminApi.deleteUser(id);
      setUsers((prev) => prev.filter((u) => u._id !== id));
      setTotal((t) => t - 1);
    } catch {}
    setConfirmDelete(null);
  };

  return (
    <SuperAdminLayout>
      <div className="space-y-6 max-w-7xl mx-auto">
        <div>
          <h1 className="text-2xl font-bold text-white">Users</h1>
          <p className="text-slate-400 text-sm mt-0.5">{total.toLocaleString()} total users across all types</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-teal-500 transition-colors"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-slate-300 focus:outline-none focus:border-teal-500 cursor-pointer"
            >
              <option value="">All types</option>
              <option value="student">Students</option>
              <option value="school_admin">School Admins</option>
              <option value="parent">Parents</option>
              <option value="super_admin">Super Admins</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-slate-300 focus:outline-none focus:border-teal-500 cursor-pointer"
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Last Login</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden xl:table-cell">Joined</th>
                  <th className="px-4 py-3.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {loading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i}>
                      {[1,2,3,4,5,6].map((j) => (
                        <td key={j} className="px-5 py-4">
                          <div className="h-4 bg-slate-800 rounded animate-pulse" style={{ width: `${50 + (j * 17) % 45}%` }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-16 text-center text-slate-500">
                      <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p>No users found</p>
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u._id} className="hover:bg-slate-800/40 transition-colors">
                      <td className="px-5 py-4">
                        <p className="font-semibold text-white">{u.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{u.email}</p>
                      </td>
                      <td className="px-4 py-4">
                        <UserTypeChip type={u.userType} />
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border capitalize ${STATUS_COLORS[u.status] ?? STATUS_COLORS.inactive}`}>
                          {u.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 hidden lg:table-cell text-slate-500 text-xs">
                        {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : "Never"}
                      </td>
                      <td className="px-4 py-4 hidden xl:table-cell text-slate-500 text-xs">
                        {new Date(u.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4">
                        <ActionMenu
                          user={u}
                          onSuspend={handleSuspend}
                          onActivate={handleActivate}
                          onDelete={(id) => setConfirmDelete(id)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-between px-5 py-3.5 border-t border-slate-800">
              <span className="text-xs text-slate-500">Page {page} of {pages}</span>
              <div className="flex gap-2">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 disabled:opacity-30 transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page === pages}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 disabled:opacity-30 transition-colors">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-sm w-full space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">Delete user?</h3>
                <p className="text-sm text-slate-400">This cannot be undone.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-semibold transition-colors">Cancel</button>
              <button onClick={() => handleDelete(confirmDelete)} className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-500 text-white text-sm font-semibold transition-colors">Delete</button>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
}
